import { productTypeMap } from 'src/shared/mappings/productTypeMap';
import { backupStatusMap } from 'src/shared/mappings/backupStatusMap';
import { custodianProfileStatusMap } from 'src/shared/mappings/custodianProfileStatusMap';
import { softwareEscrowStatusMap } from 'src/shared/mappings/softwareEscrowStatusMap';
import { isActiveStatus, isTerminatedStatus } from 'src/widgets/AppCard/model';

import type {
  CertificateBadge,
  ProductSlot,
  ProductStatus,
  ProductStatusTone,
  UnclaimedDetectedProduct,
} from 'src/widgets/AppCard/model';
import type { IAppProtectionsQuery } from 'src/graphql/actions/apps/appProtections.query';
import type { AppProductTypeEnum, EscrowStatusEnum } from 'src/graphql/schema';

const escrowStatusMap: Partial<Record<EscrowStatusEnum, ProductStatusTone>> = softwareEscrowStatusMap;
const EMPTY_STATUS_TONE: ProductStatusTone = { label: '', color: '', bgColor: '' };

type AppProtection = IAppProtectionsQuery['appProtections'][number];

const ALL_PRODUCT_TYPES = Object.keys(productTypeMap) as AppProductTypeEnum[];

const toProductStatus = (protection: AppProtection): ProductStatus => {
  switch (protection.__typename) {
    case 'Backup':
      return protection.backupStatus;
    case 'ContinuityEscrow':
    case 'SaasEscrow':
    case 'SoftwareEscrow':
      return protection.escrowStatus;
    case 'CustodianProfile':
      return protection.custodianStatus;
    default:
      throw new Error(`Unknown appProtections __typename: ${protection.__typename}`);
  }
};

// An app can hold several protections of one product type; the slot shows the most significant one.
const statusRank = (status: ProductStatus): number => {
  if (isActiveStatus(status)) return 2;
  if (isTerminatedStatus(status)) return 0;
  return 1;
};

export const mapProtectionsToProductSlots = (protections: AppProtection[]): ProductSlot[] => {
  const statusByProductType = new Map<AppProductTypeEnum, ProductStatus>();

  protections.forEach((protection) => {
    const status = toProductStatus(protection);
    const current = statusByProductType.get(protection.productType);
    if (current && statusRank(current) >= statusRank(status)) return;
    statusByProductType.set(protection.productType, status);
  });

  return ALL_PRODUCT_TYPES.map((productType) => ({
    productType,
    status: statusByProductType.get(productType) ?? null,
  }));
};

export const mapProtectionsToCertificateBadges = (protections: AppProtection[]): CertificateBadge[] => {
  const validated = protections.filter((p) => 'certificationValidated' in p && p.certificationValidated).length;

  const verified = protections.filter((p) => 'certificationVerified' in p && p.certificationVerified).length;

  const certified = protections.filter((p) => 'certificationCertified' in p && p.certificationCertified).length;

  return [
    { type: 'validated', count: validated },
    { type: 'verified', count: verified },
    { type: 'certified', count: certified },
  ];
};

const describeProtection = (
  protection: AppProtection,
): { productType: AppProductTypeEnum; name: string; status: ProductStatusTone } => {
  switch (protection.__typename) {
    case 'Backup':
      return {
        productType: protection.productType,
        name: protection.name,
        status: backupStatusMap[protection.backupStatus],
      };
    case 'CustodianProfile':
      return {
        productType: protection.productType,
        name: protection.name,
        status: custodianProfileStatusMap[protection.custodianStatus],
      };
    case 'ContinuityEscrow':
    case 'SaasEscrow':
    case 'SoftwareEscrow':
      return {
        productType: protection.productType,
        name: `${protection.depositor?.companyName ?? ''} - ${protection.beneficiary?.companyName ?? ''}`,
        status: escrowStatusMap[protection.escrowStatus] ?? EMPTY_STATUS_TONE,
      };
    default:
      throw new Error(`Unknown appProtections __typename: ${protection.__typename}`);
  }
};

export const mapProtectionsToUnclaimedDetectedProducts = (
  protections: AppProtection[],
  app: { name?: string | null },
): UnclaimedDetectedProduct[] =>
  protections.map((protection) => {
    const { productType, name, status } = describeProtection(protection);

    return {
      name,
      productType,
      status,
      vendorName: app.name ?? undefined,
    };
  });
